import { Arr } from '@deqo/foundation';
import React, { useMemo } from 'react';
import styled, { css } from 'styled-components';
import * as HelpVideos from '../../../@resources/help';
import { useObserverReturningStore } from '../../../CodesThatsGottaGoSomewhereElse';
import { Icon } from '../../../concepts/resources/Icon';
import { useWindowSizeInRem } from '../../../platform/Hook';
import { Cell, Colors, Grid, toRem } from '../../../ui';
import { Badge } from '../../../ui/Badge';
import { Roundings, Shadows } from '../../../ui/Standards';
import { TextButton } from '../../../ui/TextButton';
import { useResolve } from '../../Container';
import { Scroller } from '../../boards/Scroller';
import { Copy, Flexer, Title } from '../../dashboard/elements/LayoutElements';
import { HelpController } from './HelpController';

const ContentFrame = styled.div<{ maxHeight: number }>`
    display: flex;
    flex-direction: column;
    gap: 1rem;
    width: 32rem;
    height: 48rem;
    max-height: ${p => toRem(p.maxHeight)};
    padding: 1.5rem;
    border-radius: ${Roundings.lg};
    box-shadow: ${Shadows.lg};
    background: ${Colors.mystery};
    color: white;
    user-select: none;

    ${Title}, ${Copy} { 
        color: white;
    }
`;

// const ScrollFrame = styled.div<{ maxHeight: number }>`
//     height: 44rem;
//     max-height: ${p => toRem(p.maxHeight)};
//     margin: 0 -1rem;
// `;

const ItemGroup = styled.div`
    color: white;
    font-size: .875rem;
    font-weight: 420;
`;

const ItemLabel = styled.div`
    color: white;
    font-size: 1.25rem;
    font-weight: 450;
`;

const ItemPreview = styled.div`
    position: relative;
    height: 10rem;
    border-radius: .5rem;
    background: rgba(255, 255, 255, .2);
    background-size: cover;
    border: solid .125rem #d5acff;
`;

const ItemWipe = styled.div`
    position: absolute;
    inset: 0rem;
    background: #d5acff;
    opacity: 0;

    svg {
        position: absolute;
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%);
    }
`;

const ItemBadge = styled.div`
    position: absolute;
    top: 0;
    right: 0;
    transform: translate(-0.5rem,0.5rem);
    background: white;
    border-radius: 0.25rem;
    color: ${Colors.mystery};
    font-size: .625rem;
    font-weight: 450;
    line-height: 1em;
    padding: 0.125rem 0.375rem;
    text-align: center;
    text-transform: uppercase;
`;

const ItemFrame = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: stretch;
    gap: .25rem;
    cursor: pointer;
    border-radius: .5rem;
    overflow: hidden;

    &:hover { 
        ${ItemPreview} {
            background: rgba(255, 255, 255, .3);
        }
        ${ItemWipe} {
            opacity: 1;
            transition: none;
        }
    }
    &:active { 
        ${ItemPreview} {
            background: rgba(255, 255, 255, .4);
        }
    }

    ${Badge} {
        position: absolute;
        top: 0;
        right: 0;
        transform: translate(.5rem, -.5rem);
    }
`;

const GroupButton = styled.div<{ active?: boolean }>`
    min-width: 4rem;
    user-select: none;
    border: solid .0625rem white;
    border-radius: .5rem;
    padding: .25rem .5rem;
    color: white;
    text-align: center;
    cursor: pointer;

    &:hover {
        background: rgba(255, 255, 255, .1);
    }

    ${p => p.active && css`
        background: white !important;
        color: ${Colors.mystery};
    `}
`;

const heightMap = (wh: number) => {
    if (wh >= 70) return 40;
    return 20;
};

export const HelpLibraryView = () => {

    const ws = useWindowSizeInRem();
    const ctrl = useObserverReturningStore(useResolve(HelpController));
    const groups = [
        'All',
        ...Arr.unique(Object.values(HelpVideos).map(x => x.path.split('/')[0]?.trim()))
    ];
    const filter = ctrl.libraryFilter.toLowerCase();
    const videos = useMemo(() =>
        Object.values(HelpVideos)
            .filter(x => filter === 'all' || x.path.toLowerCase().startsWith(filter)),
        [filter]);

    return <ContentFrame maxHeight={(ws.height - 10) * .75}>
        <Flexer shrink padding=".5" flex="row" center>
            <Title flush>Guides &amp; Tutorials</Title>
        </Flexer>
        {/* <Flexer shrink flex="row" gap=".5" center>
            {groups.map(x =>
                <GroupButton
                    key={x}
                    active={x.toLowerCase() === filter}
                    onClick={() => ctrl.openLibrary(x.toLowerCase())}>
                    {x}
                </GroupButton>
            )}
        </Flexer> */}
        <Flexer grow margin="0 -1">
            <Scroller>
                <Flexer padding="0 1">
                    <Grid columns="repeat(2, 1fr)" gap="1rem">
                        {videos.sort((a, b) => a.weight - b.weight).map(x =>
                            <Cell key={x.key}>
                                <ItemFrame onClick={() => ctrl.openVideo(x)}>
                                    <Flexer inset="0 1.25" flex="row" align="center">
                                        <Flexer flex="column" >
                                            <ItemGroup>{x.path}</ItemGroup>
                                            <ItemLabel>{x.label}</ItemLabel>
                                        </Flexer>
                                    </Flexer>
                                    <ItemPreview />
                                    {ctrl.recommendedVideos.includes(x) &&
                                        <ItemBadge>Recommended</ItemBadge>
                                    }
                                    <ItemWipe>
                                        <Icon size="3" src="deqo://icons/i8/play-fill" />
                                    </ItemWipe>
                                </ItemFrame>
                            </Cell>
                        )}
                    </Grid>
                </Flexer>
            </Scroller>
        </Flexer>
        <Flexer shrink flex center>
            <TextButton size=".75" color="white" underline onClick={() => ctrl.ignoreRecommendations()}>
                Clear Recommendations
            </TextButton>
        </Flexer>
    </ContentFrame>;
};
